"use client";

import { useEffect, useRef, type CSSProperties } from "react";

import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";
import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

import type { ShopifySubpageCopy } from "./shopify-page-copy";

type CcShopifyStatementProps = {
  copy: ShopifySubpageCopy["statement"];
};

/**
 * Large statement whose words light up one by one as it scrolls through the
 * viewport (charleagency.com). The full sentence is plain text in the HTML.
 */
export function CcShopifyStatement({ copy }: CcShopifyStatementProps) {
  const ref = useRef<HTMLParagraphElement>(null);
  const reduced = useCcPrefersReducedMotion();
  const words = copy.text.split(" ");

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (reduced) {
      el.style.setProperty("--cc-shop-lit", "1");
      return;
    }
    let frame = 0;
    const update = () => {
      frame = 0;
      const rect = el.getBoundingClientRect();
      const start = window.innerHeight * 0.85;
      const end = window.innerHeight * 0.35;
      const progress = (start - rect.top) / (start - end + rect.height * 0.6);
      el.style.setProperty("--cc-shop-lit", Math.min(1, Math.max(0, progress)).toFixed(3));
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
    };
  }, [reduced]);

  return (
    <section className="cc-section cc-shop-statement" data-cc-nav="light" {...devSectionAttr("shopify-statement")}>
      <div className="cc-shop-statement__container">
        <CcSanjayaEyebrow theme="on-subtle">{copy.eyebrow}</CcSanjayaEyebrow>
        <p ref={ref} className="cc-shop-statement__text" style={{ "--cc-shop-count": words.length } as CSSProperties}>
          {words.map((word, index) => (
            <span key={`${word}-${index}`} style={{ "--cc-shop-w": index } as CSSProperties}>
              {word}{" "}
            </span>
          ))}
        </p>
      </div>
    </section>
  );
}
